import { createContext, ReactNode, useContext, useMemo } from "react";
import type { DescendantClassroom } from "../../queries/general";

/**
 * Espone i dati di `descendants-classrooms` (una sola chiamata per pagina di dettaglio)
 * alle card figlie, che altrimenti dovrebbero interrogare un endpoint per card.
 *
 * Oltre ai dati espone lo stato della chiamata, perché un record assente ha tre significati
 * diversi che le card devono poter distinguere: dato non ancora arrivato, dato che non
 * arriverà (errore o chiamata non eseguita), utente non iscritto.
 */
interface DescendantsClassroomsContextType {
  getDescendantClassroom: (loId?: number) => DescendantClassroom | undefined;
  /** Chiamata in corso: lo stato di iscrizione non è ancora noto. */
  isLoading: boolean;
  /** Chiamata fallita o non eseguita: il dato di iscrizione non arriverà. */
  isUnavailable: boolean;
  /**
   * Corso/percorso ad auto-iscrizione con approvazione la cui richiesta non è ancora stata
   * concessa: finché dura, nessun figlio è fruibile e le relative CTA restano disabilitate.
   * Viaggia nel contesto e non come prop perché le card annidate stanno sotto
   * PathCourseAccordion → AccordionActivity → ActivityDotNavigation.
   */
  approvalPending: boolean;
}

const DescendantsClassroomsContext = createContext<DescendantsClassroomsContextType | null>(null);

export const DescendantsClassroomsProvider = ({
  children,
  descendants = [],
  isLoading = false,
  isUnavailable = false,
  approvalPending = false
}: {
  children: ReactNode
  descendants?: DescendantClassroom[]
  isLoading?: boolean
  isUnavailable?: boolean
  approvalPending?: boolean
}) => {
  const value = useMemo(() => {
    const byLoId = new Map<number, DescendantClassroom>();
    descendants.forEach((item) => byLoId.set(item.idLo, item));
    return {
      approvalPending,
      getDescendantClassroom: (loId?: number) => (loId == null ? undefined : byLoId.get(loId)),
      isLoading,
      isUnavailable
    };
  }, [descendants, isLoading, isUnavailable, approvalPending]);

  return (
    <DescendantsClassroomsContext.Provider value={ value }>
      { children }
    </DescendantsClassroomsContext.Provider>
  );
};

/**
 * Fuori dal provider il dato non è semplicemente vuoto: non è disponibile.
 * Le card lo trattano come tale e ricadono sul criterio basato sulle edizioni.
 */
export const useDescendantClassrooms = (): DescendantsClassroomsContextType => {
  const context = useContext(DescendantsClassroomsContext);
  return context ?? {
    approvalPending: false,
    getDescendantClassroom: () => undefined,
    isLoading: false,
    isUnavailable: true
  };
};
